import { helper } from '@ember/component/helper';
import { get } from '@ember/object';

/**
 * Converts various input types to a plain array, including EmberData PromiseManyArray
 */
function toArray(maybeArray) {
  // Handle null/undefined
  if (maybeArray == null) {
    return [];
  }

  // Handle EmberData PromiseManyArray - check for promise-like with content
  // This maintains reactivity by accessing .content which Ember tracks
  if (typeof maybeArray === 'object' &&
      typeof maybeArray.then === 'function' &&
      'content' in maybeArray) {
    const content = maybeArray.content;

    // If content is null/undefined, return empty array
    if (content == null) {
      return [];
    }

    // Convert content to array using spread operator
    if (typeof content[Symbol.iterator] === 'function') {
      return [...content];
    }

    // If content is already an array, use it
    if (Array.isArray(content)) {
      return content;
    }

    // Recurse on content
    return toArray(content);
  }

  // Handle regular iterable objects
  if (typeof maybeArray[Symbol.iterator] === 'function') {
    return [...maybeArray];
  }

  // Already an array
  if (Array.isArray(maybeArray)) {
    return maybeArray;
  }

  // Handle array-like objects
  if (typeof maybeArray.length === 'number' && typeof maybeArray !== 'string') {
    return Array.from(maybeArray);
  }

  // Single item - wrap in array
  return [maybeArray];
}

/**
 * Compare two values for sorting
 * Handles null/undefined, numbers, strings (case-insensitive)
 */
function compare(a, b) {
  // Handle null/undefined - they should sort to the end
  const aIsNullish = a === null || a === undefined || a === '';
  const bIsNullish = b === null || b === undefined || b === '';

  if (aIsNullish && bIsNullish) return 0;
  if (aIsNullish) return 1;  // null/undefined goes after
  if (bIsNullish) return -1; // null/undefined goes after

  // Handle numbers
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  // Handle strings (case-insensitive comparison)
  const aStr = String(a);
  const bStr = String(b);
  return aStr.toLowerCase().localeCompare(bStr.toLowerCase());
}

/**
 * Parse sort key to extract property name and direction
 * Example: "name" -> { key: "name", desc: false }
 * Example: "age:desc" -> { key: "age", desc: true }
 */
function parseSortKey(sortKey) {
  const parts = sortKey.split(':');
  return {
    key: parts[0],
    desc: parts[1] === 'desc'
  };
}

/**
 * Sort array by one or more properties or by a custom sort function
 *
 * @param {Array} params - First element(s) are sort keys or function, last is the array
 * @returns {Array} - Sorted copy of the array
 *
 * Usage examples:
 *   {{sort-by "name" array}}
 *   {{sort-by "age:desc" array}}
 *   {{sort-by "lastName" "firstName" array}}
 *   {{sort-by "score:desc" "name" array}}
 *   {{sort-by this.customSortFn array}}
 */
export function sortBy(params) {
  // Last parameter is the array to sort
  const array = toArray(params[params.length - 1]);

  // Check if first parameter is a function
  if (params.length === 2 && typeof params[0] === 'function') {
    // Use the custom sort function
    return [...array].sort(params[0]);
  }

  // All parameters before the last one are sort keys
  const sortKeys = params.slice(0, -1).map(parseSortKey);

  // If no sort keys provided, return array as-is
  if (sortKeys.length === 0) {
    return array;
  }

  // Create a sorted copy (don't mutate original)
  return [...array].sort((a, b) => {
    // Compare by each sort key in order
    for (const { key, desc } of sortKeys) {
      // Use Ember's get() to support nested paths like "person.name"
      const aValue = get(a, key);
      const bValue = get(b, key);

      const comparison = compare(aValue, bValue);

      if (comparison !== 0) {
        // Apply descending order if specified
        return desc ? -comparison : comparison;
      }

      // Values are equal, continue to next sort key
    }

    // All sort keys are equal
    return 0;
  });
}

export default helper(sortBy);
